import {Inject, Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {EMPTY, Observable} from 'rxjs';
import {switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import * as _ from 'lodash';
import {ServicePointModel} from '../models/service-point.model';
import {ServicePointsHttpService} from '../services/service-points-http.service';

export interface ServicePointsComponentState {
  servicePoints: ServicePointModel[];
  cursor?: string;
  loading: boolean;
  error: string | null;
}

@Injectable()
export class ServicePointsComponentStore extends ComponentStore<ServicePointsComponentState> {

  readonly servicePoints$ = this.select((state) => state.servicePoints);
  readonly loading$ = this.select((state) => state.loading);
  readonly error$ = this.select((state) => state.error);
  readonly cursor$ = this.select((state) => state.cursor);

  vm$ = this.select({
    servicePoints: this.servicePoints$,
    loading: this.loading$,
    error: this.error$,
    cursor: this.cursor$
  });

  constructor(@Inject(ServicePointsHttpService) private servicePointsHttpService: ServicePointsHttpService) {
    super({
      servicePoints: [],
      cursor: '',
      loading: false,
      error: null,
    });
  }

  readonly onSetServicePoints = this.updater((state: ServicePointsComponentState, value: {
    servicePoints: ServicePointModel[],
    cursor?: string
  }) => ({
    ...state,
    servicePoints: _.uniqBy([...state.servicePoints, ...value.servicePoints], 'id'),
    cursor: value.cursor,
    loading: false,
    error: null,
  }));

  readonly onResetServicePoints = this.updater((state: ServicePointsComponentState) => ({
    ...state,
    servicePoints: [],
    cursor: null,
    loading: false,
    error: null,
  }));

  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  readonly setError = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    error: error.message,
    loading: false,
  }));

  readonly getServicePoints = this.effect(
    (trigger$: Observable<any>) =>
      trigger$.pipe(
        tap(() => this.setLoading(true)),
        withLatestFrom(this.cursor$),
        switchMap(([params, cursor]) =>
          this.servicePointsHttpService.getAll({...params, cursor: cursor || ''}).pipe(
            tap({
              next: (response) => this.onSetServicePoints({
                servicePoints: response.list || [],
                cursor: response.cursor
              }),
              error: (err) => this.setError(err.message || 'Failed to fetch service points'),
              finalize: () => this.setLoading(false)
            })
          )
        )
      )
  );

  readonly resetServicePoints = this.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => this.onResetServicePoints())
    )
  );
}
